<template>
  <div class="mobile-page">
    <div class="sub-nav">
      <button class="back-btn" @click="back"><el-icon :size="18"><ArrowLeft /></el-icon></button>
      <span class="nav-title">巡检记录详情</span>
      <span class="nav-right"><button class="refresh-btn" @click="load"><el-icon :size="16"><Refresh /></el-icon></button></span>
    </div>

    <div class="mobile-body">
      <div v-if="loading" class="center-box">
        <el-icon class="is-loading" :size="28"><Loading /></el-icon>
        <p class="sub-text">加载中…</p>
      </div>
      <div v-else-if="!rec" class="white-card center-box">
        <el-icon :size="40" color="#c0c4cc"><Warning /></el-icon>
        <p class="sub-text">{{ error || '未找到该记录' }}</p>
      </div>

      <template v-else>
        <!-- 概要 -->
        <div class="white-card">
          <div class="r-head">
            <span class="r-code mono">{{ rec.id }}</span>
            <span class="pill pill-blue">{{ rec.planType || '日常' }}</span>
          </div>
          <div class="r-point">{{ rec.pointName || rec.pointCode }}<span class="r-code-inline">{{ rec.pointCode }}</span></div>
          <div class="r-meta"><el-icon :size="13"><Calendar /></el-icon> {{ fmt(rec.checkTime) }}</div>
          <div class="r-divider"></div>
          <div class="info-row"><span class="info-k">巡检人</span><span class="info-v">{{ rec.inspector || '—' }}<template v-if="rec.roleType">（{{ rec.roleType }}）</template></span></div>
          <div class="info-row" v-if="rec.department"><span class="info-k">部门</span><span class="info-v">{{ rec.department }}</span></div>
          <div class="info-row" v-if="rec.area"><span class="info-k">区域</span><span class="info-v">{{ rec.area }}</span></div>
          <div class="info-row" v-if="rec.planName"><span class="info-k">计划</span><span class="info-v">{{ rec.planName }}</span></div>
          <div class="info-row" v-if="rec.identityReason"><span class="info-k">身份备注</span><span class="info-v">{{ rec.identityReason }}</span></div>
        </div>

        <!-- 媒体按需加载 -->
        <div class="white-card media-bar" v-if="!mediaLoaded && photoNeed > 0">
          <div class="load-media-btn" @click="loadMedia">
            <el-icon :size="18"><CameraFilled /></el-icon>
            <span>{{ mediaLoading ? '加载中…' : '加载现场照片（共 ' + photoNeed + ' 张）' }}</span>
          </div>
          <p class="tiny-tip">为节省流量，照片默认不下载，按需加载。</p>
        </div>

        <!-- 检查明细 -->
        <div class="section-title"><span>检查明细（{{ itemGroups.length }} 组 / {{ itemCount }} 项）</span><span v-if="rec.synced === false" class="sync">未同步</span></div>

        <div class="igroup" v-for="(g, gi) in itemGroups" :key="gi">
          <div class="ig-title">{{ g.group || '未分组' }}</div>
          <div class="iitem" v-for="(it, ii) in g.items" :key="ii">
            <div class="it-top">
              <span class="it-name">{{ it.name }}</span>
              <span v-if="it.type" class="it-type">{{ it.type }}</span>
            </div>
            <div class="it-std" v-if="it.standard">标准：{{ it.standard }}</div>
            <div class="it-result" :class="{ bad: isBad(it), normal: isGood(it) }">
              <template v-if="it.result">{{ it.result }}</template>
              <template v-else-if="it.value !== undefined && it.value !== ''">{{ it.value }}</template>
              <template v-else>—</template>
            </div>
            <div class="it-remark" v-if="it.remark">备注：{{ it.remark }}</div>
            <div v-if="it.photo" class="it-photo" @click="openPhoto(it.photo, it.name)">
              <img :src="it.photo" loading="lazy" />
              <span class="it-photo-tag">📷 现场照片</span>
            </div>
            <div v-else-if="it.hasPhoto && !mediaLoaded" class="it-photo-wait" @click="loadMedia">
              <el-icon :size="14"><Camera /></el-icon> 该检查项有照片（点击加载）
            </div>
          </div>
        </div>

        <!-- 异常项 -->
        <template v-if="(rec.hazards || []).length">
          <div class="section-title"><span>异常 / 隐患（{{ rec.hazards.length }}）</span></div>
          <div class="hazard-list">
            <div class="hz-item" v-for="(hz, hi) in rec.hazards" :key="hi">
              <span class="pill" :class="levelPill(hz.level)">{{ hz.level || '一般' }}</span>
              <div class="hz-body">
                <div class="hz-desc">{{ hz.desc }}</div>
                <div class="hz-meta" v-if="hz.remark || hz.itemName">
                  <span v-if="hz.itemName">{{ hz.itemName }}</span>
                  <span v-if="hz.remark">备注：{{ hz.remark }}</span>
                </div>
                <div class="hz-photos" v-if="hz.photoData">
                  <img :src="hz.photoData" loading="lazy" @click="openPhoto(hz.photoData, hz.desc)" />
                </div>
              </div>
            </div>
          </div>
        </template>
      </template>
    </div>

    <!-- 全屏查看器 -->
    <teleport to="body">
      <transition name="viewer">
        <div v-if="viewerVisible" class="viewer" @click.self="viewerVisible = false">
          <div class="viewer-top">
            <button @click="viewerVisible = false"><el-icon :size="22"><Close /></el-icon></button>
            <span class="viewer-count">{{ viewerIndex + 1 }} / {{ viewerList.length }}</span>
            <button @click="downloadCurrent"><el-icon :size="20"><Download /></el-icon></button>
          </div>
          <div class="viewer-body">
            <img :src="viewerList[viewerIndex]" />
          </div>
          <div class="viewer-btns">
            <button :disabled="viewerIndex === 0" @click="viewerIndex--"><el-icon><ArrowLeft /></el-icon></button>
            <button :disabled="viewerIndex >= viewerList.length - 1" @click="viewerIndex++"><el-icon><ArrowRight /></el-icon></button>
          </div>
        </div>
      </transition>
    </teleport>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { fetchInspectRecord, fetchInspectRecordMedia } from '../api'
import dayjs from 'dayjs'

const route = useRoute()
const router = useRouter()
function back() { router.back() }

const rec = ref(null)
const loading = ref(true)
const error = ref('')
const mediaLoaded = ref(false)
const mediaLoading = ref(false)

const photoNeed = computed(() => {
  if (!rec.value) return 0
  const fromItems = (rec.value.items || []).filter(it => it.hasPhoto).length
  const fromHazards = (rec.value.hazards || []).filter(hz => hz.photo || hz.photoData).length
  return fromItems + fromHazards
})

const itemGroups = computed(() => {
  const items = (rec.value?.items) || []
  const map = []
  for (const it of items) {
    const g = it.group || '未分组'
    let found = map.find(x => x.group === g)
    if (!found) {
      found = { group: g, items: [] }
      map.push(found)
    }
    found.items.push(it)
  }
  return map
})

const itemCount = computed(() => (rec.value?.items || []).length)

function fmt(t) {
  return t ? dayjs(t).format('YYYY-MM-DD HH:mm') : ''
}

function isBad(it) {
  if (!it) return false
  if (it.result === '异常') return true
  if (it.result) return /异常|故障|不合格|损坏|停用/.test(it.result)
  return false
}
function isGood(it) {
  if (!it) return false
  return it.result === '正常' || it.result === '不适用'
}
function levelPill(l) {
  if (l === '紧急' || l === '重大') return 'pill-red'
  if (l === '较大') return 'pill-orange'
  if (l === '一般') return 'pill-blue'
  return 'pill-gray'
}

/* 照片查看 */
const viewerVisible = ref(false)
const viewerList = ref([])
const viewerIndex = ref(0)

function openPhoto(src, name) {
  viewerList.value = [src]
  viewerIndex.value = 0
  viewerVisible.value = true
}

function downloadCurrent() {
  const url = viewerList.value[viewerIndex.value]
  if (!url) return
  const a = document.createElement('a')
  a.href = url
  a.download = 'photo_' + Date.now() + '.jpg'
  a.click()
}

async function load() {
  loading.value = true
  error.value = ''
  try {
    const r = await fetchInspectRecord(route.params.id)
    rec.value = r.data || null
    if (!rec.value) error.value = '记录不存在或已被删除'
  } catch (e) {
    error.value = e.message || '加载失败'
  } finally {
    loading.value = false
  }
}

async function loadMedia() {
  if (mediaLoaded.value || mediaLoading.value) return
  mediaLoading.value = true
  try {
    // 完整记录（含照片 base64）——仅本条，加载一次后即本地展示
    const r = await fetchInspectRecordMedia(route.params.id)
    const full = r.data
    if (full && full.id === (rec.value && rec.value.id)) {
      rec.value = full
    }
    mediaLoaded.value = true
  } catch (e) {
    // 媒体加载失败不阻塞文本内容查看
    mediaLoaded.value = true
    console.error('照片加载失败', e)
  } finally {
    mediaLoading.value = false
  }
}

onMounted(load)
</script>

<style scoped>
.r-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 6px; }
.r-code { font-size: 13px; font-weight: 700; color: #5f6368; }
.r-point { font-size: 17px; font-weight: 800; color: #202124; display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.r-code-inline { font-size: 12px; color: #1a73e8; background: #e8f0fe; padding: 1px 8px; border-radius: 8px; }
.r-meta { display: flex; align-items: center; gap: 5px; font-size: 12.5px; color: #9aa0a6; margin-top: 5px; }
.r-divider { height: 1px; background: #eef0f3; margin: 10px 0; }

.info-row { display: flex; gap: 10px; padding: 6px 0; font-size: 13.5px; }
.info-k { width: 62px; flex-shrink: 0; color: #9aa0a6; }
.info-v { color: #3c4043; word-break: break-all; }

.sync { font-size: 11px; color: #b06000; background: #fef7e0; padding: 1px 8px; border-radius: 8px; font-weight: 400; }

/* 媒体按需加载 */
.load-media-btn {
  display: flex; align-items: center; justify-content: center; gap: 8px;
  background: #e8f0fe; color: #1a73e8; border: 1.5px dashed #8ab4f8; border-radius: 12px;
  padding: 12px; font-size: 14px; font-weight: 600; cursor: pointer;
}
.load-media-btn:active { background: #d2e3fc; }
.tiny-tip { font-size: 11px; color: #b0b3bb; text-align: center; margin-top: 6px; }
.it-photo-wait {
  display: inline-flex; align-items: center; gap: 5px; margin-top: 7px; font-size: 12px;
  color: #1a73e8; background: #e8f0fe; border: 1px dashed #8ab4f8; border-radius: 8px;
  padding: 3px 9px; cursor: pointer;
}
.hz-photos { margin-top: 7px; display: flex; gap: 6px; flex-wrap: wrap; }
.hz-photos img { width: 76px; height: 76px; object-fit: cover; border-radius: 8px; border: 1px solid #e8eaed; cursor: pointer; }

.igroup { background: #fff; border-radius: 14px; padding: 6px 14px; margin-bottom: 10px; box-shadow: 0 3px 12px rgba(31,45,61,.05); }
.ig-title {
  font-size: 13px; font-weight: 800; color: #1a73e8; padding: 10px 0 6px;
  border-left: 4px solid #1a73e8; padding-left: 8px; margin-left: -8px;
}
.iitem { border-top: 1px solid #f2f4f7; padding: 10px 0; }
.iitem:first-of-type { border-top: none; }
.it-top { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
.it-name { font-size: 14px; font-weight: 600; color: #202124; }
.it-type { font-size: 10.5px; color: #9aa0a6; border: 1px solid #e4e7ed; border-radius: 6px; padding: 0 6px; }
.it-std { font-size: 12px; color: #9aa0a6; margin-top: 3px; }
.it-result {
  display: inline-block; margin-top: 7px; font-size: 13px; font-weight: 600;
  background: #f1f3f4; color: #5f6368; padding: 3px 12px; border-radius: 14px;
}
.it-result.normal { background: #e6f4ea; color: #188038; }
.it-result.bad { background: #fce8e6; color: #c5221f; }
.it-remark { font-size: 12.5px; color: #5f6368; margin-top: 6px; line-height: 1.5; }
.it-photo { margin-top: 8px; display: inline-block; position: relative; cursor: pointer; }
.it-photo img { width: 120px; height: 120px; object-fit: cover; border-radius: 10px; border: 1px solid #e4e7ed; }
.it-photo-tag {
  position: absolute; left: 4px; bottom: 4px; font-size: 10px; color: #fff;
  background: rgba(0,0,0,.55); padding: 1px 7px; border-radius: 8px;
}

.hazard-list { display: flex; flex-direction: column; gap: 8px; }
.hz-item { display: flex; gap: 8px; background: #fff; border-radius: 12px; padding: 11px 12px; box-shadow: 0 2px 10px rgba(31,45,61,.05); }
.hz-body { flex: 1; min-width: 0; }
.hz-desc { font-size: 13.5px; color: #3c4043; line-height: 1.5; }
.hz-meta { display: flex; gap: 8px; flex-wrap: wrap; font-size: 11.5px; color: #9aa0a6; margin-top: 4px; }
</style>
